import { viewState } from '@databases/database-state/state/view';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  type DatabaseFieldType,
} from '@databases/types/database';
import { type DatabaseView } from '@databases/types/database/view';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import { createCachedSelector } from 're-reselect';
import { snapshot } from 'valtio';

import { selectDatabaseDraftByContentId } from '../changesets/selectors';
import { type DefaultRootState } from '../redux';

import { type SelectedItem, type SelectedValue } from './slice';

/**
 * "column": One or more entire fields are selected
 * "row": One or more entire entries are selected
 * "cell": One or more individual values are selected
 */
export type ContentSelectionMode = 'column' | 'row' | 'cell';

export interface Adjacents {
  right?: boolean;
  left?: boolean;
  top?: boolean;
  bottom?: boolean;
}

const getEntryBefore = (
  entryId: string,
  { entriesById, entries }: Database
): DatabaseEntry | undefined => entriesById[entries[entries.indexOf(entryId) - 1]];
const getEntryAfter = (
  entryId: string,
  { entriesById, entries }: Database
): DatabaseEntry | undefined => entriesById[entries[entries.indexOf(entryId) + 1]];

const getFieldBefore = (
  fieldId: string,
  { fieldsById, fields }: Database
): DatabaseField | undefined => fieldsById[fields[fields.indexOf(fieldId) - 1]];
const getFieldAfter = (
  fieldId: string,
  { fieldsById, fields }: Database
): DatabaseField | undefined => fieldsById[fields[fields.indexOf(fieldId) + 1]];

export const selectFocusedItem = <T extends SelectedItem['type'] | void>(
  state: DefaultRootState,
  {
    contentId,
    type,
  }: {
    contentId: string;
    /** If specified, only returns the item if it matches the given type, otherwise return undefined. */
    type?: T;
  }
): (T extends void ? SelectedItem : Extract<SelectedItem, { type: T }>) | undefined => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });

  const result = state.cellSelections.focusedItem;
  // Return undefined if selected entry/field is not present in current database view
  if (
    result &&
    (!('entryId' in result) || database.entries.includes(result.entryId)) &&
    (!('fieldId' in result) || database.fields.includes(result.fieldId)) &&
    (!type || result.type === type)
  ) {
    return result as Extract<SelectedItem['type'], { type: T }>;
  } else {
    return undefined;
  }
};

export const getDatabaseWithCurrentView = (
  state: DefaultRootState,
  { contentId }: { contentId: string }
) => {
  const view = snapshot(viewState.view) as DatabaseView;
  return selectDatabaseDraftByContentId(state, {
    contentId,
    cacheKey: 'view',
    includeDeleted: true,
    viewSettings: view.settings,
  });
};

export const selectSelectedEntireEntryIds = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
): string[] => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  return database.entries.filter((id) =>
    state.cellSelections.selectedEntireEntries.includes(id)
  );
};

/**
 * Returns the IDs of all fields that currently have any value selected. Fields are returned
 * in the order in which they appear in the database.
 */
export const selectSelectedFieldIds = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
): string[] => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  const selectedFieldIds = new Set([
    ...state.cellSelections.selectedEntireFields,
    ...state.cellSelections.selectedValues.map((v) => v.fieldId),
  ]);
  return database.fields.filter((fieldId) => selectedFieldIds.has(fieldId));
};

/**
 * Returns true if the specified field is selected.
 */
export const isFieldSelected = (
  state: DefaultRootState,
  contentId: string,
  fieldId: string
) => {
  const selectedFieldIds = selectSelectedFieldIds(state, {
    contentId,
  });
  return selectedFieldIds.includes(fieldId);
};

/**
 * Returns the IDs of all entries that currently have any value selected. Entries are returned
 * in the order in which they appear in the database.
 */
export const selectSelectedEntryIds = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
): string[] => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  const selectedEntryIds = new Set([
    ...state.cellSelections.selectedEntireEntries,
    ...state.cellSelections.selectedValues.map((v) => v.entryId),
  ]);
  return database.entries.filter((entryId) => selectedEntryIds.has(entryId));
};

/**
 * Returns true if the specified entry is selected.
 */
export const isEntrySelected = (
  state: DefaultRootState,
  contentId: string,
  entryId: string
) => {
  const selectedEntryIds = selectSelectedEntryIds(state, {
    contentId,
  });
  return selectedEntryIds.includes(entryId);
};

export const selectSelectedEntireFieldIds = (
  state: DefaultRootState,
  { contentId }: { contentId: string }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  return database.fields.filter((id) =>
    state.cellSelections.selectedEntireFields.includes(id)
  );
};

export const selectFieldIdsByContentId = (
  state: DefaultRootState,
  { contentId }: { contentId: string }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  return database.fields;
};

/**
 * Returns the number of selected entire fields.
 * Returns 0 in "row" and "cell" selection modes.
 */
export const selectSelectedEntireFieldsCount = (
  state: DefaultRootState,
  { contentId }: { contentId: string }
): number => {
  return selectSelectedEntireFieldIds(state, { contentId }).length;
};

/**
 * Returns the number of selected entire entries.
 * Returns 0 in "field" and "cell" selection modes.
 */
export const selectSelectedEntriesCount = (
  state: DefaultRootState,
  { contentId }: { contentId: string }
): number => selectSelectedEntireEntryIds(state, { contentId }).length;

export const selectSelectedValues = (
  state: DefaultRootState,
  { contentId }: { contentId: string }
): SelectedValue[] => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  return state.cellSelections.selectedValues.filter(
    (v) => database.entries.includes(v.entryId) && database.fields.includes(v.fieldId)
  );
};

/**
 * Returns the number of values currently selected.
 */
export const selectSelectedValuesCount = (
  state: DefaultRootState,
  { contentId }: { contentId: string }
): number => {
  return selectSelectedValues(state, { contentId }).length;
};

export const selectCurrentSelectionMode = createCachedSelector<
  DefaultRootState,
  { contentId: string },
  number,
  number,
  ContentSelectionMode
>(
  (state, { contentId }) => selectSelectedEntireFieldsCount(state, { contentId }),
  (state, { contentId }) => selectSelectedEntriesCount(state, { contentId }),
  (fieldsCount, entriesCount) => {
    if (fieldsCount > 0) {
      return 'column';
    } else if (entriesCount > 0) {
      return 'row';
    } else {
      return 'cell';
    }
  }
)((state, { contentId }) => contentId);

export const selectIsDeleteColumnsDisabled = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
): boolean => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  const hasOneField = database.fields.length === 1;
  const selectedEntireFieldIds = selectSelectedEntireFieldIds(state, {
    contentId,
  });
  const selectedFieldIds = selectSelectedFieldIds(state, {
    contentId,
  });
  const currentSelectionMode = selectCurrentSelectionMode(state, {
    contentId,
  });
  return (
    (currentSelectionMode === 'column' &&
      (database.fields.length === selectedEntireFieldIds.length || hasOneField)) ||
    (currentSelectionMode === 'cell' &&
      database.fields.length === selectedFieldIds.length) ||
    (currentSelectionMode === 'row' &&
      database.fields.length === selectedFieldIds.length) ||
    hasOneField
  );
};

export const selectHasSelectedCells = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
): boolean => {
  const selectedEntireFieldIds = selectSelectedEntireFieldIds(state, {
    contentId,
  });
  const selectedEntireEntryIds = selectSelectedEntireEntryIds(state, {
    contentId,
  });
  return (
    selectedEntireFieldIds.length > 0 ||
    selectedEntireEntryIds.length > 0 ||
    selectSelectedValuesCount(state, { contentId }) > 0
  );
};

export const selectIsValueSelected = (
  state: DefaultRootState,
  {
    entryId,
    fieldId,
  }: {
    entryId: string;
    fieldId: string;
  }
): boolean =>
  state.cellSelections.selectedEntireEntries.includes(entryId) ||
  state.cellSelections.selectedEntireFields.includes(fieldId) ||
  state.cellSelections.selectedValues.some(
    (v) => v.fieldId === fieldId && v.entryId === entryId
  );

export const selectIsSoftSelectEnabled = (state: DefaultRootState): boolean =>
  state.cellSelections.softSelectEnabled;

export const selectIsHoverDeleteEntriesButton = (state: DefaultRootState): boolean =>
  state.cellSelections.hoverDeleteButton === 'entries';

export const selectIsHoverDeleteFieldsButton = (state: DefaultRootState): boolean =>
  state.cellSelections.hoverDeleteButton === 'fields';

export const selectIsEntireFieldSelected = (
  state: DefaultRootState,
  {
    fieldId,
  }: {
    fieldId: string;
  }
) => state.cellSelections.selectedEntireFields.includes(fieldId);

export const selectIsEntireEntrySelected = (
  state: DefaultRootState,
  {
    entryId,
  }: {
    entryId: string;
  }
) => state.cellSelections.selectedEntireEntries.includes(entryId);

/**
 * Returns the selected adjacent cells for the specified cell.
 */
export function selectSelectedAdjacentCells(
  state: DefaultRootState,
  {
    contentId,
    entryId,
    fieldId,
  }: {
    contentId: string;
    entryId?: string;
    fieldId: string;
  }
): Adjacents {
  const isRightSelected = selectIsRightAdjacentSelected(state, {
    contentId,
    entryId,
    fieldId,
  });

  const isLeftSelected = selectIsLeftAdjacentSelected(state, {
    contentId,
    entryId,
    fieldId,
  });

  const isTopSelected =
    !!entryId &&
    selectIsTopAdjacentSelected(state, {
      contentId,
      entryId,
      fieldId,
    });

  const isBottomSelected =
    !!entryId &&
    selectIsBottomAdjacentSelected(state, {
      contentId,
      entryId,
      fieldId,
    });

  return {
    top: isTopSelected,
    bottom: isBottomSelected,
    left: isLeftSelected,
    right: isRightSelected,
  };
}

// Exported for tests
export const selectIsRightAdjacentSelected = (
  state: DefaultRootState,
  {
    contentId,
    fieldId,
    entryId,
  }: {
    contentId: string;
    fieldId: string;
    entryId?: string;
  }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });

  const rightFieldId = getFieldAfter(fieldId, database)?.id;
  return (
    !!rightFieldId &&
    (entryId
      ? selectIsValueSelected(state, { entryId, fieldId: rightFieldId })
      : selectIsEntireFieldSelected(state, {
          fieldId: rightFieldId,
        }))
  );
};

// Exported for tests
export const selectIsLeftAdjacentSelected = (
  state: DefaultRootState,
  {
    contentId,
    fieldId,
    entryId,
  }: {
    contentId: string;
    fieldId: string;
    entryId?: string;
  }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });

  const leftFieldId = getFieldBefore(fieldId, database)?.id;
  return (
    !!leftFieldId &&
    (entryId
      ? selectIsValueSelected(state, { fieldId: leftFieldId, entryId })
      : selectIsEntireFieldSelected(state, { fieldId: leftFieldId }))
  );
};

export const selectHasFieldBeforeSelection = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  const selectedFieldIds = selectSelectedFieldIds(state, {
    contentId,
  });

  return selectedFieldIds.every((fieldId) => !!getFieldBefore(fieldId, database)?.id);
};

export const selectHasFieldAfterSelection = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });

  const selectedFieldIds = selectSelectedFieldIds(state, {
    contentId,
  });

  return selectedFieldIds.every((fieldId) => !!getFieldAfter(fieldId, database)?.id);
};

export const selectHasEntryBeforeSelection = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });
  const selectedEntryIds = selectSelectedEntryIds(state, {
    contentId,
  });

  return selectedEntryIds.every((entryId) => !!getEntryBefore(entryId, database)?.id);
};

export const selectHasEntryAfterSelection = (
  state: DefaultRootState,
  {
    contentId,
  }: {
    contentId: string;
  }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });

  const selectedEntryIds = selectSelectedEntryIds(state, {
    contentId,
  });

  return selectedEntryIds.every((entryId) => !!getEntryAfter(entryId, database)?.id);
};

// Exported for tests
export const selectIsTopAdjacentSelected = (
  state: DefaultRootState,
  {
    contentId,
    fieldId,
    entryId,
  }: {
    contentId: string;
    fieldId: string;
    entryId: string;
  }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });

  const topEntryId = getEntryBefore(entryId, database)?.id;
  return topEntryId
    ? selectIsValueSelected(state, { fieldId, entryId: topEntryId })
    : selectIsEntireFieldSelected(state, { fieldId }); // If the entire field is selected, the column header is also selected.
};

// Exported for tests
export const selectIsBottomAdjacentSelected = (
  state: DefaultRootState,
  {
    contentId,
    fieldId,
    entryId,
  }: {
    contentId: string;
    fieldId: string;
    entryId: string;
  }
) => {
  const database = getDatabaseWithCurrentView(state, {
    contentId,
  });

  const bottomEntryId = getEntryAfter(entryId, database)?.id;
  return (
    !!bottomEntryId && selectIsValueSelected(state, { fieldId, entryId: bottomEntryId })
  );
};

export const selectFirstSelectedValue = (state: DefaultRootState) =>
  state.cellSelections.firstSelectedValue;

export function selectedFieldsMatchType(
  database: Database,
  state: DefaultRootState,
  type: DatabaseFieldType
) {
  return selectSelectedFieldIds(state, { contentId: database.contentId }).every(
    (fieldId) => {
      const field = getDatabaseFieldRequired(database, fieldId);
      return field.type === type;
    }
  );
}

/**
 * Returns selected cells in a format suitable for AIFC serialization.
 * Returns a Set of cell keys in the format "entryId:fieldId".
 *
 * This includes:
 * - Individual selected cells
 * - All cells in selected entire fields (columns)
 * - All cells in selected entire entries (rows)
 */
export function selectSelectedCellsForSerialization(
  state: DefaultRootState,
  {
    contentId,
    database,
  }: {
    contentId: string;
    database: { entries: readonly string[]; fields: readonly string[] };
  }
): Set<string> {
  const selectedEntireFields = selectSelectedEntireFieldIds(state, { contentId });
  const selectedEntireEntries = selectSelectedEntireEntryIds(state, { contentId });
  const selectedValues = selectSelectedValues(state, { contentId });

  const cellKeys = new Set<string>();

  for (const { entryId, fieldId } of selectedValues) {
    cellKeys.add(`${entryId}:${fieldId}`);
  }

  for (const fieldId of selectedEntireFields) {
    for (const entryId of database.entries) {
      cellKeys.add(`${entryId}:${fieldId}`);
    }
  }

  for (const entryId of selectedEntireEntries) {
    for (const fieldId of database.fields) {
      cellKeys.add(`${entryId}:${fieldId}`);
    }
  }

  return cellKeys;
}
